/**
 * Server-side pagination footer + state hook, extracted from the
 * Employees page so every large table paginates the same way.
 *
 *     const pager = usePagination(resetKey);          // resetKey: filters/search/sort
 *     // fetch with ?page=${pager.page}&page_size=${pager.pageSize}
 *     <PaginationFooter
 *       pager={pager}
 *       count={data?.count}
 *       hasNext={!!data?.next}
 *       isFetching={query.isFetching}
 *     />
 *
 * `resetKey` is compared by Object.is — pass a primitive (e.g. a
 * JSON.stringify of the filter state) so changing any filter snaps the
 * pager back to page 1.
 */
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { useUiPreference } from "@/lib/use-ui-pref";
import { cn } from "@/lib/utils";

export const DEFAULT_PAGE_SIZES = [10, 25, 50, 100] as const;

// Below this many rows even the smallest page size shows everything on
// one page, so the whole control is noise — hide it.
const MIN_ROWS_TO_PAGINATE = 10;

export interface PaginationState {
  page: number;
  setPage: (next: number | ((p: number) => number)) => void;
  pageSize: number;
  setPageSize: (next: number) => void;
}

export function usePagination(
  resetKey?: unknown,
  initialPageSize = 50,
): PaginationState {
  const [page, setPage] = useState(1);
  // Rows-per-page is a single per-user preference persisted server-side
  // (mirrored to localStorage), so the choice survives navigating between
  // tables and a logout/login. The current page number stays ephemeral —
  // resuming on page 7 of a different dataset would be wrong.
  const [pageSize, setPageSize] = useUiPreference<number>(
    "pagination.page-size",
    initialPageSize,
  );

  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  return { page, setPage, pageSize, setPageSize };
}

interface PaginationFooterProps {
  pager: PaginationState;
  /** Total row count from the paginated response; footer hides until known. */
  count: number | undefined;
  /** Whether the response reported a next page. */
  hasNext: boolean;
  isFetching?: boolean;
  pageSizes?: readonly number[];
  className?: string;
}

export function PaginationFooter({
  pager,
  count,
  hasNext,
  isFetching,
  pageSizes = DEFAULT_PAGE_SIZES,
  className,
}: PaginationFooterProps) {
  if (count === undefined || count <= MIN_ROWS_TO_PAGINATE) return null;

  const first = (pager.page - 1) * pager.pageSize + 1;
  const last = Math.min(pager.page * pager.pageSize, count);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 text-sm text-slate",
        className,
      )}
    >
      <span className="tabular-nums">
        Showing {first.toLocaleString("en-PH")}–{last.toLocaleString("en-PH")}{" "}
        of {count.toLocaleString("en-PH")}
      </span>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2">
          <span className="text-xs">Rows per page</span>
          <Select
            value={String(pager.pageSize)}
            onChange={(e) => pager.setPageSize(Number(e.target.value))}
            className="h-8 w-auto py-0"
            aria-label="Rows per page"
          >
            {pageSizes.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={pager.page <= 1 || isFetching}
          onClick={() => pager.setPage((p) => Math.max(1, p - 1))}
          aria-label="Previous page"
        >
          <ChevronLeft size={15} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasNext || isFetching}
          onClick={() => pager.setPage((p) => p + 1)}
          aria-label="Next page"
        >
          <ChevronRight size={15} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
